import { useEffect, useState } from "react";
import { apiAxios } from "../../utils/axios";
import { RequestName } from "./apiHookTypes";

/**
 * Whether the open-port check may be offered at all (owner decision
 * 2026-10-21: the feature stays invisible until its probes exist).
 *
 * `GET /port-check/availability` -> `{status:"success", data:{available}}`.
 * Public: sent with NO Authorization header, so the 401 key-recovery
 * interceptor (which acts only on requests carrying the user's key) never
 * touches it.
 *
 * Fetched ONCE per page load: the promise is cached at module level and
 * shared by every mounted consumer. Fails CLOSED — any error, timeout, non-2xx
 * or malformed body means "not available", and so does "not answered yet".
 */

let availabilityRequest: Promise<boolean> | null = null;

const fetchAvailability = async (): Promise<boolean> => {
  try {
    const response = await apiAxios.get(`/${RequestName.PORT_CHECK_AVAILABILITY}`, {
      headers: { "X-Client-Source": "site" },
    });
    const payload = response?.data;
    return payload?.status === "success" && payload?.data?.available === true;
  } catch {
    return false;
  }
};

/** The shared once-per-page-load lookup. */
export const getPortCheckAvailability = (): Promise<boolean> => {
  if (!availabilityRequest) availabilityRequest = fetchAvailability();
  return availabilityRequest;
};

/** Test seam: forget the cached answer so the next consumer refetches. */
export const resetPortCheckAvailability = (): void => {
  availabilityRequest = null;
};

/** `true` only once the api has confirmed the feature is available. */
export const usePortCheckAvailability = (): boolean => {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let active = true;
    getPortCheckAvailability().then((value) => {
      if (active) setAvailable(value);
    });
    return () => {
      active = false;
    };
  }, []);

  return available;
};
